import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import About from './pages/About';
import Writing from './pages/Writing';
import Contact from './pages/Contact';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import TheYoungSamaritan from './pages/TheYoungSamaritan';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { useGSAP } from '@gsap/react';
import PageNotFound from './components/404';
import TYSReviews from './pages/TYSReviews';
import { clearLenis, getLenis, setLenis } from './utils/lenis';

gsap.registerPlugin(ScrollTrigger);

function App() {
  const location = useLocation();

  useEffect(() => {
    const Lenis = window.Lenis;

    ScrollTrigger.config({
      ignoreMobileResize: true,
    });

    if (typeof Lenis !== 'function') {
      return undefined;
    }

    const lenis = new Lenis();
    const syncScrollTrigger = () => ScrollTrigger.update();
    const advanceLenisFrame = (time) => {
      lenis.raf(time * 1000);
    };

    setLenis(lenis);
    lenis.on('scroll', syncScrollTrigger);
    gsap.ticker.add(advanceLenisFrame);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(advanceLenisFrame);
      lenis.off?.('scroll', syncScrollTrigger);
      lenis.destroy();
      clearLenis(lenis);
    };
  }, []);

  useGSAP(
    () => {
      const elements = gsap.utils.toArray('.animate-text');

      if (!elements.length) {
        return undefined;
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        gsap.set(elements, { clearProps: 'all', autoAlpha: 1 });
        return undefined;
      }

      gsap.set(elements, { autoAlpha: 0, y: 18 });

      ScrollTrigger.batch(elements, {
        start: 'top 88%',
        once: true,
        onEnter: (batch) => {
          gsap.to(batch, {
            autoAlpha: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.12,
            ease: 'power3.out',
            overwrite: 'auto',
          });
        },
      });

      return () => {
        gsap.set(elements, { clearProps: 'transform,opacity,visibility' });
      };
    },
    { dependencies: [location.pathname], revertOnUpdate: true }
  );

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    const restoreScroll = () => {
      const lenis = getLenis();

      if (lenis) {
        lenis.scrollTo(0, { immediate: true, force: true });
        return;
      }

      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    restoreScroll();
    const rafId = window.requestAnimationFrame(() => {
      restoreScroll();
      ScrollTrigger.refresh();
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [location.pathname]);

  return (
    <div className='App' id='top'>
      <Navbar />
      <Routes location={location} key={location.pathname}>
        <Route index element={<TheYoungSamaritan />} />
        <Route path='/home' element={<TheYoungSamaritan />} />
        <Route path='/about' element={<About />} />
        <Route path='/writing' element={<Writing />} />
        <Route path='/contact' element={<Contact />} />
        <Route
          path='/writing/the-young-samaritan'
          element={<TheYoungSamaritan />}
        />
        <Route
          path='/writing/the-young-samaritan/reviews'
          element={<TYSReviews />}
        />
        <Route path='*' element={<PageNotFound />} />
      </Routes>
      <Footer />
      <div className='bottom-padding'></div>
    </div>
  );
}

export default App;
